import { apiUrl } from "../api/endpoints";
import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useAuth } from "../hooks/AuthProvider";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";

import "../widgets/widget.css";

import DataGridTable from "../Components/DataGridTable";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { Tooltip } from "@mui/material";

import { LanguageContext } from "../context/LanguageContext";
import roleTranslations from "../languages/role.json";

import { getErrorMessage } from "../utils/errorUtils";
import AppDialog from "../Components/AppDialog"; // <-- adjust path if needed

import { AddRole } from "./AddRole";
import { EditRole } from "./EditRole";
import { deleteRole, deleteMultiRoles } from "../lib/actions";
import { normalizeRoleListRow } from "../utils/listRowIds";
import Spinner from "../Components/Spinner";

export const Roles = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);
  const [addRole, setAddRole] = useState(false);
  const [editRole, setEditRole] = useState({ open: false, id: 0 });

  const { lang } = useContext(LanguageContext);
  const t = roleTranslations[lang] || {};
  const isRtl = ["ar", "ur"].includes(lang);

  const { permissions, user } = useAuth();
  const pagingoptions = [10, 20, 50, 100];


  const canViewRoles = permissions["view_roles"];
  const canAddRoles = permissions["add_roles"] ;
  const canEditRoles = permissions["edit_roles"] ;
  const canDeleteRoles = permissions["delete_roles"] ;

  // ---------------------------
  // AppDialog "alert" function
  // ---------------------------
  const resolverRef = useRef(null);
  const [dialog, setDialog] = useState({
    open: false,
    type: "alert", // "alert" | "confirm"
    variant: "info", // "info" | "danger" | "error"
    icon: "alert", // "alert" | "delete"
    title: "",
    message: "",
    confirmText: "Ok",
    cancelText: "Cancel",
    showCancel: false,
  });

  const closeDialog = useCallback((result = false) => {
    setDialog((d) => ({ ...d, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const alert = useCallback(
    (opts) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;

        setDialog({
          open: true,
          type: opts?.type || "alert",
          variant: opts?.variant || "info",
          icon: opts?.icon || "alert",
          title: opts?.title || "",
          message: opts?.message || "",
          confirmText: opts?.confirmText || "Ok",
          cancelText: opts?.cancelText || "Cancel",
          showCancel: Boolean(opts?.showCancel),
        });
      }),
    []
  );

  // Keep same API name used in this component previously
  const showAlert = useCallback(
    async (type, message) => {
      const variant =
        type === "error" ? "error" : type === "warning" ? "info" : "info";

      await alert({
        type: "alert",
        variant,
        icon: "alert",
        title:
          type === "error"
            ? (t.alert?.errorTitle || "Error")
            : type === "warning"
            ? (t.alert?.warningTitle || "Warning")
            : (t.alert?.infoTitle || "Info"),
        message: message || "",
        confirmText: "Ok",
      });
    },
    [alert, t.alert?.errorTitle, t.alert?.warningTitle, t.alert?.infoTitle]
  );

  const baseColumns = [
    {
      field: "name",
      headerName: t.column?.name || "Name",
      width: 300,
      align: "left",
      headerAlign: "left",
      headerClassName: "font-responsive",
      cellClassName: "font-responsive",
    },
    {
      field: "number_of_users",
      headerName: t.column?.userCount || "User Count",
      width: 150,
      align: "center",
      headerAlign: "center",
      headerClassName: "font-responsive",
      cellClassName: "font-responsive",
    },
  ];

  const canShowActionsColumn = canEditRoles || canDeleteRoles;

  const columns = canShowActionsColumn
    ? [
        {
          field: "actions",
          headerName: t.column?.actions || "Actions",
          type: "actions",
          width: 100,
          getActions: (params) => {
            const isReservedRole = ["Owner", "Super"].includes(params.row.name);

            return [
              canEditRoles && !isReservedRole ? (
                <GridActionsCellItem
                  icon={
                    <Tooltip title={t.tooltip?.edit || "Edit"}>
                      <EditIcon
                        style={{
                          fontSize:
                            "calc(var(--body-font-size) * var(--font-size-multiplier))",
                        }}
                      />
                    </Tooltip>
                  }
                  showInMenu
                  label={t.tooltip?.edit || "Edit"}
                  onClick={() =>
                    setEditRole({ open: true, id: params.row.role_id })
                  }
                />
              ) : null,

              canDeleteRoles && !isReservedRole ? (
                <GridActionsCellItem
                  icon={
                    <Tooltip title={t.tooltip?.delete || "Delete"}>
                      <DeleteIcon
                        style={{
                          fontSize:
                            "calc(var(--body-font-size) * var(--font-size-multiplier))",
                        }}
                      />
                    </Tooltip>
                  }
                  showInMenu
                  label={t.tooltip?.delete || "Delete"}
                  onClick={() => deleteRole(params.row.role_id, fetchRoles, alert)}
                />
              ) : null,
            ].filter(Boolean);
          },
        },
        ...baseColumns,
      ]
    : baseColumns;

  const fetchRoles = useCallback(async () => {
    if (!canViewRoles) {
      setRows([]);
      setRowCount(0);
      return;
    }

    setLoading(true);

    try {
      const activeSort = sortModel && sortModel.length > 0 ? sortModel[0] : { field: "name", sort: "asc" };
      const res = await axios.get(apiUrl(`roles`), {
        params: {
          page,
          pageSize,
          sortField: activeSort.field || "name",
          sortOrder: activeSort.sort || "asc",
          filterModel: JSON.stringify(filterModel || { items: [] }),
        },
      });

      if (res.data && Array.isArray(res.data.data)) {
        const mappedRows = res.data.data.map(normalizeRoleListRow);
        setRows(mappedRows);
        setRowCount(res.data?.pagination?.total ?? res.data.data.length);
      } else {
        console.error("Received unexpected data format from /roles:", res.data);
        setRows([]);
        setRowCount(0);
        await showAlert(
          "error",
          t.alert?.invalidFormat || "Received an invalid format from the server."
        );
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      if (err.response && err.response.status === 403) {
        await showAlert(
          "error",
          t.permissionDenied?.message ||
            "You do not have permission to view roles."
        );
      } else {
        await showAlert("error", errorMessage);
      }
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    canViewRoles,
    filterModel,
    page,
    pageSize,
    showAlert,
    sortModel,
    t.alert?.invalidFormat,
    t.permissionDenied?.message,
  ]);

  useEffect(() => {
      fetchRoles();
  }, [fetchRoles]);

  const [selectedRows, setSelectedRows] = React.useState([]);

  const selectActions = canDeleteRoles
    ? [
        {
          title: t.bulkActions?.delete || "Delete Selected",
          icon: (
            <DeleteIcon
              style={{
                color: "rgb(236, 69, 78)",
                marginInlineEnd: "20px",
                fontSize:
                  "calc(var(--body-font-size) * var(--font-size-multiplier))",
              }}
            />
          ),
          action: async () => {
            const deletableIds = rows
              .filter(
                (r) =>
                  selectedRows.includes(r.id) &&
                  !["Owner", "Super"].includes(r.name)
              )
              .map((r) => r.role_id);

            if (deletableIds.length !== selectedRows.length) {
              await showAlert(
                "warning",
                t.alert?.ownerProtected ||
                  "Reserved roles were included in the selection and cannot be deleted."
              );
            }

            if (deletableIds.length > 0) {
              await deleteMultiRoles(deletableIds, fetchRoles, alert);
            }

            setSelectedRows([]);
          },
        },
      ].filter(Boolean)
    : [];

  const handleRowClick = (params) => {
    if (canEditRoles) {
      setEditRole({ open: true, id: params.row.role_id });
    }
  };

  if (!canViewRoles) {
    return (
      <div className="configuration" style={{ padding: "20px" }}>
        <h5 style={{ color: "var(--text-color-light)" }}>
          {t.permissionDenied?.title || "Permission Denied"}
        </h5>
        <p>
          {t.permissionDenied?.message ||
            "You do not have permission to view roles."}
        </p>
      </div>
    );
  }

  return (
    <div
      className="configuration devices-page"
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <AppDialog
        open={dialog.open}
        type={dialog.type}
        variant={dialog.variant}
        icon={dialog.icon}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        showCancel={dialog.showCancel}
        onConfirm={() => closeDialog(true)}
        onCancel={() => closeDialog(false)}
        onClose={() => closeDialog(false)}
      />

      {addRole ? <AddRole setState={setAddRole} refresh={fetchRoles} /> : null}
      {editRole.open ? (
        <EditRole roleId={editRole.id} setState={setEditRole} refresh={fetchRoles} />
      ) : null}

      <div className="device-main">
        <div className="devices-header">
          <div className="devices-header-left">
            <h2 className="devices-title">{t.rolesPageTitle || "Roles Management"}</h2>
          </div>

          {canAddRoles ? (
            <button className="devices-primary-btn" onClick={() => setAddRole(true)}>
              <AddIcon style={{ marginInlineEnd: "8px" }} />
              <span>{t.newRole || "New Role"}</span>
            </button>
          ) : null}
        </div>

        {loading && rows.length === 0 && rowCount === 0 && !(filterModel?.items || []).length ? (
          <Spinner />
        ) : rows.length > 0 || rowCount > 0 || (filterModel?.items || []).length > 0 ? (
          <div className="table-wrapper" style={{ flex: 1, minHeight: 0 }}>
            <DataGridTable
              columns={columns}
              rows={rows}
              idcolumn={"id"}
              pagingoptions={pagingoptions}
              sortField="name"
              sortType="asc"
              handleRowClick={handleRowClick}
              checkboxSelection={canDeleteRoles}
              rowSelectionModel={selectedRows}
              setRowSelectionModel={setSelectedRows}
              selectActions={selectActions}
              showToolbar={true}
              tableKey="roles"
              loading={loading}
              pagination={{
                serverSide: true,
                page,
                pageSize,
                count: rowCount,
                sortModel,
                onPageChange: (newPage) => setPage(newPage),
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                },
                onSortModelChange: (newModel) => {
                  setSortModel(newModel && newModel.length ? newModel : []);
                  setPage(1);
                },
                onFilterModelChange: (newModel) => {
                  setFilterModel(newModel || { items: [] });
                  setPage(1);
                },
              }}
            />
          </div>
        ) : (
          <div
            style={{
              width: "100%",
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))",
            }}
          >
            {t.noData || "No roles available."}
          </div>
        )}
      </div>
    </div>
  );
};
